import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { useChangeCache } from "@/hooks/use-change-cache";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  ComputeHiddenPlaceholdersState,
  HiddenWaypointsComputer,
  MapWaypointsRenderer,
  MapWaypointsRendererRef,
  useClipFrustum,
  useThreeEventTargetRef,
} from "@faro-lotv/app-component-toolbox";
import { grey, yellow } from "@faro-lotv/flat-ui";
import { EMPTY_ARRAY, GUID } from "@faro-lotv/foundation";
import {
  isRevisionScanEntity,
  isSiteScapeClusterEntity,
  RevisionScanEntity,
} from "@faro-lotv/service-wires";
import { Box } from "@mui/system";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { shallowEqual } from "react-redux";
import { Color, Plane, Vector3 } from "three";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { useEditConnectionContext } from "../edit-connection-context";
import ScanMarkerSvg from "../icons/registration-marker.svg";
import {
  selectEntityIdToColorMap,
  selectHighlightedScanIds,
  selectHoveredEntityId,
  selectRevisionEntityWorldPositions,
  selectSelectedEntityIds,
  selectVisibilityMap,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setHoveredEntityId,
  toggleMultiSelection,
  toggleSingleSelection,
  unsetHoveredEntityId,
} from "../store/data-preparation-ui/data-preparation-ui-slice";
import { selectScanMarkersVisibilityMode } from "../store/data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanMarkersVisibilityMode } from "../store/data-preparation-view-options/data-preparation-view-options-slice";
import { selectHierarchicalRegistrationStatusMap } from "../store/registration-status-selectors";
import {
  selectRevisionEntityChildrenMap,
  selectRevisionEntityMap,
} from "../store/revision/revision-entity-selectors";
import {
  selectIsAddingConnections,
  selectIsTransformingScans,
} from "../store/user-edits/data-preparation-user-edit-selectors";
import { Projection } from "../ui/projection-switch";
import { PREVIEW_CLOSE_DELAY_MS, ScanPreview } from "../ui/scan-preview";
import { HOVER_FACTOR, RenderOrder } from "../utils/entity-utils";
import { normalizeSelection } from "../utils/normalize-selection";
import {
  determineLabelAssignment,
  useApplyLabelAssignment,
  useScanMarkerLabelTextures,
  useTextureVariants,
} from "./canvas-scan-marker-labels";
import { TOOLTIP_Z_INDEX } from "./registration-connections";

/** Size of a scan placeholder. */
export const PLACEHOLDER_SIZE = 20;
/**
 * Warning placeholder variants have a thicker border.
 * To keep the size of the inner circle consistent, the total size is increased.
 */
export const PLACEHOLDER_WARNING_SIZE = 24;

/** Add an offset to the popper to position it just below the scan marker. */
const POPPER_MODIFIERS = [
  {
    name: "offset",
    options: {
      offset: [0, (PLACEHOLDER_SIZE * HOVER_FACTOR) / 3],
    },
  },
];

/** Delay in milliseconds before highlighting the scan. */
const HOVER_DELAY_MS = 50;

type CanvasScanMarkersProps = {
  /** The scan entities to render the scan markers for. */
  scanEntities: RevisionScanEntity[];
  /** Clipping planes to clip the scan markers with. */
  clippingPlanes?: Plane[];
};

/** @returns Scan markers that can handle high scan counts without dropping performance. */
export function CanvasScanMarkers({
  scanEntities,
  clippingPlanes,
}: CanvasScanMarkersProps): JSX.Element | null {
  const { projection } = useDataPreparationOverlayElements();
  const defaultTextures = useTextureVariants(ScanMarkerSvg);
  const entityIdToColorMap = useAppSelector(selectEntityIdToColorMap);
  const statusMap = useAppSelector(selectHierarchicalRegistrationStatusMap);

  const { handleAddConnection, sourceId } = useEditConnectionContext();

  const isTransformingScans = useAppSelector(selectIsTransformingScans);

  const { getState, dispatch } = useAppStore();

  const scanEntityIds = useMemo(
    () => scanEntities.map((scanEntity) => scanEntity.id),
    [scanEntities],
  );
  const positions = useAppSelector(
    selectRevisionEntityWorldPositions(scanEntityIds),
  );

  const computeHidden = useHiddenScanMarkerComputer(
    scanEntities,
    clippingPlanes,
  );

  // Determine which scan entities require special labels
  const labelAssignment = useMemo(
    () => determineLabelAssignment(scanEntities, statusMap),
    [scanEntities, statusMap],
  );

  // Configure the renderer
  const [waypointsRenderer, setWaypointRenderer] =
    useState<MapWaypointsRendererRef | null>(null);
  useEffect(() => {
    if (waypointsRenderer) {
      // Use the same texture for each interaction state (default, hovered, selected)
      // This increases the amount of different scan marker icons that can be used
      waypointsRenderer.material.interactionTextureVariants = false;
    }
  }, [waypointsRenderer]);
  // Expose the scan ID per marker, keyed by its index, so individual markers can be identified
  // (e.g. in E2E tests via playwright-three's `getInstanceById`).
  useEffect(() => {
    if (waypointsRenderer) {
      waypointsRenderer.geometry.userData.id = scanEntityIds;
    }
  }, [waypointsRenderer, scanEntityIds]);
  useApplyLabelAssignment(waypointsRenderer, labelAssignment);
  useScanMarkerLabelTextures(waypointsRenderer);

  // Occlude scan markers in 3D mode to reduce visual clutter and improve depth perception
  useEffect(() => {
    if (!waypointsRenderer) return;

    const depthTesting = projection === Projection.threeDimensional;

    waypointsRenderer.material.depthTest = depthTesting;
    waypointsRenderer.material.depthWrite = true;
    waypointsRenderer.material.needsUpdate = true;
  }, [waypointsRenderer, projection]);

  // Whether the scan marker is directly hovered, or the scan through the tree view
  const [isHoveredDirectly, setIsHoveredDirectly] = useState(false);
  const [isMarkerHovered, setIsMarkerHovered] = useState(false);
  const hoveredScanId = useAppSelector(selectHoveredEntityId);
  const hoveredScanIdx = useMemo(() => {
    if (!hoveredScanId) return;
    const idx = scanEntityIds.indexOf(hoveredScanId);
    return idx >= 0 ? idx : undefined;
  }, [hoveredScanId, scanEntityIds]);

  const [previewAnchorEl, setPreviewAnchorEl] = useState<
    HTMLElement | undefined
  >(undefined);
  const [previewPosition, setPreviewPosition] = useState<Vector3 | undefined>(
    undefined,
  );
  const isAddingConnections = useAppSelector(selectIsAddingConnections);
  const portal = useThreeEventTargetRef();
  const showScanPreview =
    hoveredScanId && isHoveredDirectly && !isAddingConnections;

  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // Whether the pointer is currently over the preview popover. Kept in a ref so
  // the (delayed) hover-cleanup callbacks can read the latest value without
  // being re-created on every hover change.
  const isPreviewHoveredRef = useRef(false);
  const highlightedScanIds = useAppSelector(selectHighlightedScanIds);

  const colors = useMemo(
    () =>
      scanEntities.map((entity) => {
        const isHighlighted =
          highlightedScanIds.includes(entity.id) || sourceId === entity.id;

        return isHighlighted
          ? new Color(entityIdToColorMap[entity.id])
          : new Color(grey[500]);
      }),
    [scanEntities, entityIdToColorMap, highlightedScanIds, sourceId],
  );
  const secondaryColors = useMemo(
    () => scanEntities.map(() => new Color(yellow[500])),
    [scanEntities],
  );

  const selectedEntities = useAppSelector(selectSelectedEntityIds);
  const entityMap = useAppSelector(selectRevisionEntityMap);
  const childrenMap = useAppSelector(selectRevisionEntityChildrenMap);
  const selectedScanIds = useMemo(() => {
    const selectedScanIds = new Set<GUID>();

    for (const entityId of selectedEntities) {
      const entity = entityMap[entityId];
      if (!entity) continue;

      // For SiteScape, highlight all scan markers in the capture,
      // because they are treated as one unit
      if (isSiteScapeClusterEntity(entity)) {
        const children = childrenMap[entity.id] ?? EMPTY_ARRAY;

        for (const child of children) {
          selectedScanIds.add(child.id);
        }
      } else if (isRevisionScanEntity(entity)) {
        selectedScanIds.add(entity.id);
      }
    }

    return selectedScanIds;
  }, [selectedEntities, entityMap, childrenMap]);
  const selectedScanIdx = useMemo(() => {
    const selectedScanIdx = [];

    for (const [idx, scanId] of scanEntityIds.entries()) {
      // Also highlight first scan when adding a new connection for visual feedback
      if (selectedScanIds.has(scanId) || sourceId === scanId) {
        selectedScanIdx.push(idx);
      }
    }

    return selectedScanIdx;
  }, [selectedScanIds, scanEntityIds, sourceId]);

  // If the user is editing add a connection, otherwise toggle the scan selection
  const addConnectionOrToggleSelection = useCallback(
    (idx: number, event: MouseEvent) => {
      const scanEntity = scanEntities[idx];
      const state = getState();

      if (handleAddConnection(scanEntity.id)) {
        return;
      }

      const selectionId = normalizeSelection(state, scanEntity.id);

      // Otherwise, toggle selection
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        // Multi-selection with shift, ctrl (windows) or cmd (mac)
        dispatch(toggleMultiSelection(selectionId));
      } else {
        dispatch(toggleSingleSelection(selectionId));
      }
    },
    [dispatch, getState, handleAddConnection, scanEntities],
  );

  // Hide the hover preview and reset the related hover state.
  const clearHoverPreview = useCallback(() => {
    isPreviewHoveredRef.current = false;
    dispatch(unsetHoveredEntityId());
    setIsHoveredDirectly(false);
    setPreviewPosition(undefined);
  }, [dispatch]);

  const onCanvasMarkerHovered = useCallback(
    (idx: number | undefined) => {
      // Clear any pending hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }

      // If the user is currently in the process of moving the scans, do not show any hover effects
      // They cause state updates and rendering changes that cause lag during editing
      if (isTransformingScans) {
        setIsMarkerHovered(false);
        clearHoverPreview();
        return;
      }

      if (idx === undefined) {
        setIsMarkerHovered(false);
        // Delay hiding the preview so the user can move the pointer onto the
        // popover to interact with the panorama. The close is cancelled if the
        // preview becomes hovered (see onPreviewHoverChange).
        hoverTimeoutRef.current = setTimeout(() => {
          if (isPreviewHoveredRef.current) return;
          clearHoverPreview();
        }, PREVIEW_CLOSE_DELAY_MS);
        return;
      }

      const scanEntity = scanEntities.at(idx);
      if (scanEntity) {
        setIsMarkerHovered(true);
        // Show pano and hovered scan after a delay
        hoverTimeoutRef.current = setTimeout(() => {
          dispatch(setHoveredEntityId(scanEntity.id));
          setPreviewPosition(positions[idx]);
          setIsHoveredDirectly(true);
        }, HOVER_DELAY_MS);
      }
    },
    [dispatch, scanEntities, positions, isTransformingScans, clearHoverPreview],
  );

  // Called when the pointer enters or leaves the preview popover. Keeps the
  // preview open while hovered so the user can rotate the panorama, and hides
  // it again (after a short delay) once the pointer leaves.
  const onPreviewHoverChange = useCallback(
    (hovered: boolean) => {
      isPreviewHoveredRef.current = hovered;

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }

      if (!hovered) {
        hoverTimeoutRef.current = setTimeout(
          clearHoverPreview,
          PREVIEW_CLOSE_DELAY_MS,
        );
      }
    },
    [clearHoverPreview],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <>
      {/* For some reason, applying the renderOrder directly on the MapWaypointsRenderer does not work,
     it causes the markers to be rendered behind the point clouds */}
      <group name="capture markers" renderOrder={RenderOrder.placeholders}>
        <MapWaypointsRenderer
          ref={setWaypointRenderer}
          waypoints={positions}
          customColors={colors}
          customSecColors={secondaryColors}
          textures={defaultTextures}
          baseSize={PLACEHOLDER_SIZE}
          hoveredSizeFactor={HOVER_FACTOR}
          selectedSizeFactor={HOVER_FACTOR}
          hoveredId={hoveredScanIdx}
          multipleSelectedIds={selectedScanIdx}
          onPlaceholderClick={addConnectionOrToggleSelection}
          onPlaceholderHovered={onCanvasMarkerHovered}
          computeHidden={computeHidden}
          channelBlending
        />
      </group>
      {/* Single shared preview instance */}
      {hoveredScanId && (
        <AppAwareHtml
          position={previewPosition}
          style={{
            margin: 0,
            padding: 0,
            lineHeight: 0,
            transition: "opacity 0.5s ease, transform 0.5s ease",
            opacity: showScanPreview ? 1 : 0,
            transform: showScanPreview ? "scale(1)" : "scale(0.95)",
            display: showScanPreview ? undefined : "none",
          }}
          renderOrder={RenderOrder.placeholders}
          zIndexRange={[TOOLTIP_Z_INDEX, TOOLTIP_Z_INDEX]}
          portal={portal}
        >
          <Box component="div" ref={setPreviewAnchorEl} />
          <ScanPreview
            scanId={hoveredScanId}
            isHovering={isMarkerHovered}
            isVisible={!!showScanPreview}
            anchorEl={previewAnchorEl}
            disablePortal={false}
            popperModifiers={POPPER_MODIFIERS}
            onPreviewHoverChange={onPreviewHoverChange}
          />
        </AppAwareHtml>
      )}
    </>
  );
}

/**
 * @param scanEntities The scan entities to get the hidden indices for.
 * @param clippingPlanes Clipping planes to clip the scan markers with.
 * @returns A function to return the indexes of the hidden scan entities, including the clipped ones.
 */
function useHiddenScanMarkerComputer(
  scanEntities: RevisionScanEntity[],
  clippingPlanes?: Plane[],
): HiddenWaypointsComputer {
  const scanMarkerVisibilityMode = useAppSelector(
    selectScanMarkersVisibilityMode,
  );
  const visibilityMap = useAppSelector(selectVisibilityMap);
  const clipFrustum = useClipFrustum(clippingPlanes);

  const { hiddenScanIndices, hiddenScanIndexMask } = useMemo(() => {
    let hiddenScanIndices: number[];

    switch (scanMarkerVisibilityMode) {
      case ScanMarkersVisibilityMode.showNone:
        // No scan markers visible -> all indexes are hidden
        hiddenScanIndices = [...scanEntities.keys()];
        break;
      case ScanMarkersVisibilityMode.showAll:
        // All scan markers visible -> no indexes are hidden
        hiddenScanIndices = EMPTY_ARRAY;
        break;
      case ScanMarkersVisibilityMode.showVisible: {
        hiddenScanIndices = [];
        // Only hide scan markers of hidden scans
        for (const [idx, scanEntity] of scanEntities.entries()) {
          if (visibilityMap[scanEntity.id] === false) {
            hiddenScanIndices.push(idx);
          }
        }
        break;
      }
    }

    // Create a mask for efficient look-up in the frequently called useCallback below.
    const hiddenScanIndexMask = new Uint8Array(scanEntities.length);
    for (const hiddenIndex of hiddenScanIndices) {
      hiddenScanIndexMask[hiddenIndex] = 1;
    }
    return { hiddenScanIndices, hiddenScanIndexMask };
  }, [scanEntities, scanMarkerVisibilityMode, visibilityMap]);

  const hasChanged = useChangeCache();
  const previousHiddenRef = useRef<number[]>(hiddenScanIndices);

  return useCallback(
    (state: ComputeHiddenPlaceholdersState) => {
      if (!clipFrustum || hiddenScanIndices.length === scanEntities.length) {
        // The hasChanged cache needs to be updated for the check below to detect a change.
        hasChanged("changeInfoArray", undefined);
        previousHiddenRef.current = hiddenScanIndices;
        return hiddenScanIndices;
      }

      // By checking if the frustum and hidden indices have changed, it can be avoided to iterate
      // over hundreds of scan markers and calling containsPoint for each of them unless the user
      // is currently manipulating the clipping box (or changed visibility).
      // It's sufficient to check the length of the hiddenScanIndices array, because there's no
      // way for the user to change the selection of hidden scan markers while keeping the same
      // length in a single operation.
      // For optimal performance of the comparison, it's therefore possible to put all numbers in
      // a single array.
      const changeInfoArray: number[] = [];
      changeInfoArray.push(hiddenScanIndices.length);
      for (const plane of clipFrustum.planes) {
        changeInfoArray.push(plane.constant);
        changeInfoArray.push(plane.normal.x);
        changeInfoArray.push(plane.normal.y);
        changeInfoArray.push(plane.normal.z);
      }

      if (!hasChanged("changeInfoArray", changeInfoArray, shallowEqual)) {
        return previousHiddenRef.current;
      }

      // Must stay inside the callback to update clipped markers continuously while clipping box
      // manipulators are dragged.
      const hiddenIndices: number[] = [];
      const { positions } = state;
      const { length } = positions;

      for (let idx = 0; idx < length; idx++) {
        if (
          hiddenScanIndexMask[idx] === 1 ||
          !clipFrustum.containsPoint(positions[idx])
        ) {
          hiddenIndices.push(idx);
        }
      }

      previousHiddenRef.current = hiddenIndices;
      return hiddenIndices;
    },
    [
      hasChanged,
      clipFrustum,
      hiddenScanIndices,
      hiddenScanIndexMask,
      scanEntities.length,
    ],
  );
}
